<template>
  <TsCarouselV2
    v-if="props.tabs.length > 0"
    ref="mainCarouselRef"
    :pagination="false"
    :drag-free="true"
    :append-class="twMerge('px-0 -m-1 flex', props.appendClass, alignmentCss)"
    viewport-class="pb-2"
    prev-button-class="-mt-1"
    next-button-class="-mt-1"
  >
    <template v-for="(tab, index) in props.tabs">
      <TsCarouselSlide
        v-if="featureFlags.enableNewTrolleyJourney"
        :key="tab.id"
        :append-class="twMerge('flex-[0_0_auto] px-2 lg:px-3 py-1', props.bordered && 'p-0 lg:p-0')"
      >
        <div
          v-if="tab.image"
          class="flex min-w-[124px] cursor-pointer flex-col items-center transition-transform duration-200 ease-in-out hover:-translate-y-1"
          @click="setActiveTag(tab, index)"
        >
          <div
            class="mb-3 flex h-[100px] w-[100px] items-center justify-center overflow-hidden rounded-full border border-[#e1e9f4] bg-white shadow-[0_4px_12px_rgba(0,0,0,0.05)]"
            :class="{ 'border-primary ring-2 ring-primary ring-offset-2': props.active === tab.id }"
          >
            <TsImage :src="tab.image" :alt="tab.label" class="h-4/5 w-4/5 object-contain" />
          </div>

          <TsTypography
            variant="sm"
            weight="semibold"
            align="center"
            size="sm"
            append-class="max-w-[110px] leading-tight text-[#333] font-['Inter',_sans-serif] line-clamp-2 overflow-hidden text-ellipsis break-words [hyphens:auto]"
            :class="{ 'text-primary': props.active === tab.id }"
          >
            {{ tab.label }}
          </TsTypography>
        </div>
        <TsButton
          v-else
          :label="tab.label"
          :link="tab.link && typeof tab.link === 'string' ? tab.link : ''"
          :icon="tab.icon"
          :append-class="
            twMerge(
              'px-3 focus:ring-0 text-nowrap border-[#E9E9E9] max-sm:shadow-[0px_4px_12px_0px_#E3E3E399] cursor-pointer',
              props.active !== tab.id &&
                `bg-idle-white text-natural-grey hover:text-white font-semibold ${props.activeButtonClass}`,
              props.noShadow && 'max-sm:shadow-none shadow-none',
              props.bordered &&
                `border-0 rounded-none bg-transparent text-gray-500 border-b-2 hover:bg-transparent hover:text-gray-500 ${props.active === tab.id && 'border-b-primary text-primary hover:text-primary'}`,
              props.buttonClass
            )
          "
          rounded
          :data-testid="`${props.dataTestid}-${tab.id}-tab`"
          size="sm"
          @click="setActiveTag(tab, index)"
        />
      </TsCarouselSlide>
    </template>
  </TsCarouselV2>
</template>

<script setup lang="ts">
import { twMerge } from 'tailwind-merge';
import { ref, onMounted, nextTick, watch, defineAsyncComponent } from 'vue';
import { scrollTo } from '~/utils/carouselUtils';

const TsCarouselSlide = defineAsyncComponent(
  () => import('~/components/organisms/TsCarouselSlide.vue')
);

const TsCarouselV2 = defineAsyncComponent(() => import('~/components/organisms/TsCarouselV2.vue'));

const mainCarouselRef = ref<any>(null);
const mainCarouselApi = ref<any>(null);
const selected_index = ref(0);
const config = useRuntimeConfig();
const featureFlags = config.public.featureFlags;

type Tag = {
  id?: string;
  label: string;
  link?: string;
  icon?: string;
  image?: string;
};

type Props = {
  tabs?: Tag[];
  active?: number | string;
  align?: 'left' | 'center' | 'right';
  dataTestid?: string;
  noShadow?: boolean;
  bordered?: boolean;
  appendClass?: string;
  buttonClass?: string;
  activeButtonClass?: string;
};

const emit = defineEmits(['tabClick']);

const props = withDefaults(defineProps<Props>(), {
  tabs: () => [
    { id: '1', label: 'All' },
    { id: '2', label: 'All - 2' },
    { id: '3', label: 'All - 3' },
  ],
  align: 'left',
  active: '1',
  dataTestid: 'molecule-tabs-category-selection',
  appendClass: '',
  buttonClass: '',
  activeButtonClass: '',
});

const setActiveTag = (tab: Tag, index: number) => {
  selected_index.value = index;
  emit('tabClick', tab, index);
};

const alignmentCss =
  props.align === 'center'
    ? 'justify-center'
    : props.align === 'left'
      ? 'justify-start'
      : props.align === 'right'
        ? 'justify-end'
        : '';

onMounted(() => {
  nextTick(() => {
    if (mainCarouselRef.value) {
      mainCarouselApi.value = (mainCarouselRef.value as any)?.emblaApi;
    }
  });
});

watch(
  () => props.active,
  (newActive) => {
    if (mainCarouselRef.value && mainCarouselApi.value && props.tabs && props.tabs.length > 0) {
      const activeIndex = props.tabs.findIndex((item) => item?.id === newActive);
      if (activeIndex !== -1) {
        selected_index.value = activeIndex;
        scrollTo(mainCarouselApi, selected_index.value);
      }
    }
  },
  { immediate: true }
);
</script>
